import { Link } from "react-router-dom";
import type { Review } from "@/mocks/reviews";
import { formatViews, getMovieById } from "@/utils/catalog";

interface ReviewCardProps {
  review: Review;
  variant?: "card" | "row" | "mini";
}

export default function ReviewCard({ review, variant = "card" }: ReviewCardProps) {
  const movie = getMovieById(review.movieId);

  if (variant === "mini") {
    return (
      <Link
        to={`/reviews/${review.id}`}
        className="group flex items-start gap-3 border-b border-background-200/80 py-3 last:border-none"
      >
        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-primary-50 text-[10px] font-semibold text-primary-700">
          {review.views > 15000 ? "热" : "评"}
        </span>
        <span className="min-w-0">
          <span className="block line-clamp-2 text-[13px] leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
            {review.title}
          </span>
          <span className="mt-1 flex items-center gap-2 text-[10px] text-foreground-400">
            <span>{review.date}</span>
            <span className="h-2.5 w-px bg-background-300" />
            <span className="flex items-center gap-0.5">
              <i className="ri-eye-line text-[11px]" />
              {formatViews(review.views)}
            </span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "row") {
    return (
      <article className="group flex flex-col gap-3 border-b border-background-200 py-5 first:pt-0 last:border-none sm:flex-row sm:gap-5">
        <div className="w-full shrink-0 sm:w-[104px]">
          <span className="block overflow-hidden rounded-md bg-background-200">
            <span className="block aspect-[16/10] w-full sm:aspect-[2/3]">
              <img
                src={movie?.cover}
                alt={`${movie?.title ?? "电影"}封面`}
                title={`${movie?.title ?? "电影"} - 相关影评`}
                className="h-full w-full object-top object-cover transition-transform duration-500 group-hover:scale-105"
              />
            </span>
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
            <span className="rounded-full bg-primary-50 px-2 py-0.5 text-primary-700">
              {movie?.genres[0] ?? "影评"}
            </span>
            <span>{review.date}</span>
            <span className="h-3 w-px bg-background-300" />
            <span className="flex items-center gap-1">
              <i className="ri-eye-line text-xs" />
              {formatViews(review.views)} 次浏览
            </span>
          </div>
          <Link to={`/reviews/${review.id}`} className="mt-2 block">
            <h3 className="font-heading text-base leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
              {review.title}
            </h3>
          </Link>
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600 md:text-[13px]">
            {review.excerpt}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-foreground-500">
            <span>作者：{review.author}</span>
            {movie ? (
              <Link to={`/detail/${movie.id}`} className="text-primary-600 hover:text-primary-700">
                相关电影：{movie.title}
              </Link>
            ) : null}
          </div>
        </div>
      </article>
    );
  }

  return (
    <article className="group flex h-full flex-col rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300">
      <div className="flex items-center justify-between gap-2">
        <span className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-800">
          {movie?.title ?? "影视随笔"}
        </span>
        <span className="flex items-center gap-1 text-[11px] text-foreground-400">
          <i className="ri-eye-line text-xs" />
          {formatViews(review.views)}
        </span>
      </div>
      <Link to={`/reviews/${review.id}`} className="mt-3">
        <h3 className="line-clamp-2 font-heading text-[17px] leading-snug text-foreground-950 transition-colors group-hover:text-primary-700">
          {review.title}
        </h3>
      </Link>
      <p className="mt-2.5 line-clamp-3 flex-1 text-[13px] leading-relaxed text-foreground-600">
        {review.excerpt}
      </p>
      <div className="mt-4 flex items-center justify-between border-t border-background-200/80 pt-3 text-[11px] text-foreground-500">
        <span>{review.author}</span>
        <span>{review.date}</span>
      </div>
    </article>
  );
}